import CloseIcon from '@mui/icons-material/Close';
import TripOriginRoundedIcon from '@mui/icons-material/TripOriginRounded';

import { Box, Button, Divider, IconButton, Popover, Typography, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColorResult, CustomPicker, HSLColor, InjectedColorProps, RGBColor } from 'react-color';
import { Hue, Saturation } from 'react-color/lib/components/common';

import { getActionPrefix, rgbToHex } from '../../../../utils/helpers';
import { ResetButton } from '../../_controls_';
import {
  StyledFooter,
  StyledHeader,
  StyledInputsWrapper,
  StyledPickerContainer,
  StyledPreviewColorComponent,
  StyledSaturationWrapper,
  StyledSliderWrapper,
} from './ColorPicker.styles';
import { HexInput } from './HexInput';

interface IProps {
  hex?: string;
  rgb?: RGBColor;
  hsl?: HSLColor;
  onChange?: (color: ColorResult) => void;
  onChangeComplete?: (color: ColorResult) => void;
  onResetColor?: () => void;
  dataAction?: string;
}

const pointerBaseStyle = {
  width: 14,
  height: 14,
  color: 'white',
  filter: 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.5))',
  cursor: 'pointer',
} as const;

const pointerStyles = {
  ...pointerBaseStyle,
  transform: 'translate(-7px, -7px)',
} as const;

const sliderPointerStyles = {
  ...pointerBaseStyle,
  transform: 'translate(-6px, 0px)',
} as const;

interface ICustomPickerProps extends InjectedColorProps {
  hsv?: {
    h: number;
    s: number;
    v: number;
    a?: number;
  };
  onChangeComplete?: (color: ColorResult) => void;
  onClose: () => void;
}

const CustomColorPickerComponent: React.FC<ICustomPickerProps> = ({
  hex,
  hsl,
  hsv,
  onChange,
  onChangeComplete,
  onClose,
}) => {
  const theme = useTheme();

  const Pointer = useCallback(() => <TripOriginRoundedIcon sx={pointerStyles} />, []);

  const SliderPointer = useCallback(() => <TripOriginRoundedIcon sx={sliderPointerStyles} />, []);

  const onHexInputChange = useCallback(
    (_hexValue: string, colorResult?: ColorResult) => {
      if (colorResult) {
        onChange?.(colorResult);
      }
    },
    [onChange],
  );

  const sliders = useMemo(() => {
    return (
      <StyledSliderWrapper>
        <Hue {...({ hsl, onChange, onChangeComplete, pointer: SliderPointer, direction: 'horizontal' } as any)} />
      </StyledSliderWrapper>
    );
  }, [hsl, onChange, onChangeComplete, SliderPointer]);

  const inputs = useMemo(() => {
    return (
      <StyledFooter>
        <Typography variant="body2" sx={{ fontWeight: theme.typography.fontWeightMedium, alignSelf: 'center' }}>
          Hex
        </Typography>
        <StyledInputsWrapper>
          <HexInput
            value={hex || `${theme.palette.common.black}`}
            onChange={onHexInputChange}
            hsl={hsl}
            dataAction="color_picker"
          />
        </StyledInputsWrapper>
      </StyledFooter>
    );
  }, [hex, theme.palette.common.black, theme.typography.fontWeightMedium, onHexInputChange, hsl]);

  const header = useMemo(() => {
    return (
      <StyledHeader>
        <Typography variant="body2" sx={{ fontWeight: theme.typography.fontWeightMedium }}>
          Color
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ width: 22, height: 22, p: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          data-action="close_color_picker_btn"
        >
          <CloseIcon sx={{ width: 16, height: 16, color: theme.palette.action.active }} />
        </IconButton>
      </StyledHeader>
    );
  }, [theme.typography.fontWeightMedium, onClose, theme.palette.action.active]);

  return (
    <Box>
      {header}
      <Divider sx={{ my: 0 }} />
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <StyledSaturationWrapper>
          <Saturation {...({ hsl, hsv, pointer: Pointer, onChange, onChangeComplete } as any)} />
        </StyledSaturationWrapper>
        {sliders}
        {inputs}
      </Box>
    </Box>
  );
};

interface ICustomColorPickerProps {
  color: string | RGBColor;
  onChange?: (color: ColorResult) => void;
  onChangeComplete?: (color: ColorResult) => void;
  onClose: () => void;
}

const CustomColorPicker = CustomPicker<ICustomPickerProps & InjectedColorProps>(
  CustomColorPickerComponent,
) as React.ComponentType<ICustomColorPickerProps>;

export const ColorPicker: React.FC<IProps> = ({
  hex: initialHex,
  rgb: initialRgb,
  hsl: initialHsl,
  onChange,
  onChangeComplete,
  onResetColor,
  dataAction,
}) => {
  const dataActionPrefix = useMemo(() => getActionPrefix(dataAction), [dataAction]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [colorState, setColorState] = useState<{
    hex: string;
    rgb: RGBColor;
    hsl: HSLColor;
    alpha: number;
  }>(() => {
    const hex = initialHex || rgbToHex(initialRgb?.r || 0, initialRgb?.g || 0, initialRgb?.b || 0);
    return {
      hex,
      rgb: initialRgb || { r: 0, g: 0, b: 0, a: 1 },
      hsl: initialHsl || { h: 0, s: 0, l: 0, a: 1 },
      alpha: initialRgb?.a !== undefined ? initialRgb.a : 1,
    };
  });

  useEffect(() => {
    const hex = initialHex || rgbToHex(initialRgb?.r || 0, initialRgb?.g || 0, initialRgb?.b || 0);
    setColorState({
      hex,
      rgb: initialRgb || { r: 0, g: 0, b: 0, a: 1 },
      hsl: initialHsl || { h: 0, s: 0, l: 0, a: 1 },
      alpha: initialRgb?.a !== undefined ? initialRgb.a : 1,
    });
  }, [initialHex, initialRgb, initialHsl]);

  const handleOpenPicker = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClosePicker = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Opacity controls were removed from the picker — every new pick emits a solid color (a: 1).
  // Previously saved colors with alpha still render in the preview swatch until re-picked.
  const handleColorChange = useCallback(
    (newColor: ColorResult) => {
      setColorState((prev) => ({
        hex: newColor.hex || prev.hex,
        rgb: newColor.rgb,
        hsl: newColor.hsl,
        alpha: 1,
      }));

      onChange?.({
        ...newColor,
        rgb: { ...newColor.rgb, a: 1 },
        hsl: { ...newColor.hsl, a: 1 },
      });
    },
    [onChange],
  );

  const handleColorChangeComplete = useCallback(
    (newColor: ColorResult) => {
      setColorState((prev) => ({
        hex: newColor.hex || prev.hex,
        rgb: newColor.rgb,
        hsl: newColor.hsl,
        alpha: 1,
      }));

      const callback = onChangeComplete || onChange;
      callback?.({
        ...newColor,
        rgb: { ...newColor.rgb, a: 1 },
        hsl: { ...newColor.hsl, a: 1 },
      });
    },
    [onChange, onChangeComplete],
  );

  const handleHexInputChange = useCallback(
    (_hexValue: string, colorResult?: ColorResult) => {
      if (colorResult) {
        setColorState((prev) => ({
          hex: colorResult.hex || prev.hex,
          rgb: colorResult.rgb,
          hsl: colorResult.hsl,
          alpha: 1,
        }));
        const callback = onChangeComplete || onChange;
        callback?.({
          ...colorResult,
          rgb: { ...colorResult.rgb, a: 1 },
          hsl: { ...colorResult.hsl, a: 1 },
        });
      }
    },
    [onChange, onChangeComplete],
  );

  const resetButton = useMemo(() => {
    return <ResetButton onClick={onResetColor!} dataAction={`${dataActionPrefix}reset_color_btn`} />;
  }, [onResetColor, dataActionPrefix]);

  const isOpen = useMemo(() => Boolean(anchorEl), [anchorEl]);

  const popover = useMemo(() => {
    return (
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClosePicker}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          mt: 1,
        }}
        data-action={`${dataActionPrefix}color_picker_popover`}
      >
        <CustomColorPicker
          color={colorState.rgb || colorState.hex}
          onChange={handleColorChange}
          onChangeComplete={handleColorChangeComplete}
          onClose={handleClosePicker}
        />
      </Popover>
    );
  }, [
    isOpen,
    anchorEl,
    handleClosePicker,
    dataActionPrefix,
    colorState.rgb,
    colorState.hex,
    handleColorChange,
    handleColorChangeComplete,
  ]);

  return (
    <>
      <StyledPickerContainer>
        <Box sx={{ width: 'fit-content', display: 'flex', gap: 2 }}>
          <Button
            variant="text"
            onClick={handleOpenPicker}
            aria-label="Open color picker"
            sx={{
              p: 0,
              minWidth: 0,
              cursor: 'pointer',
            }}
            data-action={`${dataActionPrefix}color_picker_btn`}
          >
            <StyledPreviewColorComponent rgb={colorState.rgb} alpha={colorState.alpha} />
          </Button>
          <HexInput
            value={colorState.hex}
            onChange={handleHexInputChange}
            hsl={colorState.hsl}
            alpha={colorState.alpha}
            height={32}
            fontSize="16px"
            dataAction={`${dataActionPrefix}hex_input`}
          />
        </Box>
        {onResetColor && resetButton}
      </StyledPickerContainer>

      {popover}
    </>
  );
};
